import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Appearance } from 'design/types';
import { SettingsSectionContent } from 'screens/Settings/components';
import { DaySegmentItem } from 'screens/SiteSettings/DaySegments/components';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { DaySegments, generateDaySegmentsRequest, replaceDaySegmentsRequest } from 'store/Collections/DaySegments';
import { IStore } from 'store/types';
import { ICreateUpdateDaySegmentRequest, IDaySegment } from 'types';
import { AlertHeader, Button, Message, ModalFactory, ModalFooter, ModalTitle, Text } from '../../../../components';
import { theme, toRem } from '../../../../design';
import { LoadingText, ModalContent, StyledSpinner } from '../../TipCalculator/components/styles';

interface IAIDaySegmentsModalProps {
  siteId: string;
  onClose(): void;
  dataCy?: string;
}

const Content = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${toRem(16)};

  p.center {
    text-align: center;
  }
`;

export const AIDaySegmentsModal: React.FC<IAIDaySegmentsModalProps> = ({ siteId, onClose, dataCy = 'ai-day-segments-modal' }) => {
  const isMounted = useRef(false);
  const dispatch = useDispatch();
  const [daySegments, setDaySegments] = useState<ICreateUpdateDaySegmentRequest[]>(null);
  const [error, setError] = useState<any>(null);
  const isGeneratingDaySegments = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, DaySegments.GENERATE_DAY_SEGMENTS_REQUEST));
  const isReplacingDaySegments = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, DaySegments.REPLACE_DAY_SEGMENTS_REQUEST));

  const handleSuccess = (daySegments: ICreateUpdateDaySegmentRequest[]) => {
    setDaySegments(daySegments);
  };

  const handleError = (error: any) => {
    setError(error);
  };

  const handleSave = (siteId: string, daySegments: ICreateUpdateDaySegmentRequest[]) => {
    dispatch(replaceDaySegmentsRequest(siteId, daySegments, handleSaveSuccess));
  };

  const handleSaveSuccess = () => {
    onClose();
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      dispatch(generateDaySegmentsRequest(siteId, handleSuccess, handleError));
    }
  }, [dispatch, siteId]);

  return (
    <ModalFactory dataCy={dataCy} onClose={onClose} isActive maxWidth={600} closeOnOutSideClick={false}>
      <Content>
        {isGeneratingDaySegments && (
          <>
            <ModalTitle>Generating Day Segments</ModalTitle>
            <StyledSpinner color={theme.color.secondary[400]} size={64} stroke={6} />
            <LoadingText>Please wait while we generate the day segments</LoadingText>
          </>
        )}
        {!isGeneratingDaySegments && daySegments && (
          <>
            <ModalTitle>Review Day Segments</ModalTitle>
            <Text size="small" className="center">
              The day segments below have been generated by reviewing your POS data. Please view before applying these day segments.
            </Text>
            <SettingsSectionContent data-cy="ai-day-segments-modal-rule">
              {daySegments.map((rule) => (
                <DaySegmentItem key={rule.name} daySegment={rule as IDaySegment} readOnly />
              ))}
            </SettingsSectionContent>
            <ModalFooter columns={2}>
              <Button appearance="white" label="Cancel" data-cy={`${dataCy}-cancel-button`} onClick={() => onClose()} />
              <Button appearance="primary" label="Save" data-cy={`${dataCy}-save-button`} isProcessing={isReplacingDaySegments} onClick={() => handleSave(siteId, daySegments)} />
            </ModalFooter>
          </>
        )}
        {!isGeneratingDaySegments && !daySegments && error && (
          <>
            <AlertHeader size={64} />
            <ModalTitle>We are having trouble</ModalTitle>
            <Text>
              We encountered an issue generating the
              <br />
              day segments for this site.
            </Text>
            {error.errorMessages.length && <Message className="message" appearance={Appearance.WARNING} message={error.errorMessages[0]} />}
          </>
        )}
      </Content>
    </ModalFactory>
  );
};
